import { createServer } from "node:http";
import { buildManifest, newAppUrl, convertManifestCode, type ConvertedApp } from "./github-app-manifest.js";
import { LocalCliError } from "./errors.js";

export function renderManifestForm(args: { org: string; state: string; redirectUrl: string; setupUrl: string; nameSuffix: string }): string {
  const manifest = buildManifest({ org: args.org, redirectUrl: args.redirectUrl, setupUrl: args.setupUrl, nameSuffix: args.nameSuffix });
  return `<!doctype html><html><head><meta charset="utf-8"></head><body>
<p>Redirecting you to GitHub's pre-filled "Create GitHub App" page…</p>
<form id="f" action="${newAppUrl(args.org, args.state)}" method="post">
<input type="hidden" name="manifest" id="manifest">
<noscript><button type="submit">Continue to GitHub</button></noscript>
</form>
<script>
document.getElementById("manifest").value = JSON.stringify(${JSON.stringify(manifest)});
document.getElementById("f").submit();
</script></body></html>`;
}

export type InstallState = "awaiting-install" | "installed" | "error";

const PAGE_STYLE = `<style>
body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;max-width:560px;margin:48px auto;padding:0 20px;line-height:1.5;color:#1a1a1a}
.btn{display:inline-block;background:#1f6feb;color:#fff;padding:10px 18px;border-radius:8px;text-decoration:none;font-weight:600;font-size:15px}
.muted{color:#666;font-size:14px}
</style>`;
const shell = (body: string) => `<!doctype html><html><head><meta charset="utf-8">${PAGE_STYLE}</head><body>${body}</body></html>`;

const INSTALLED_HTML = (org: string) =>
  `<h3>✅ Installed on ${org} — you're all set.</h3><p>Return to your terminal.</p>`;
const ERROR_HTML = (org: string) =>
  `<h3>⚠ Couldn't confirm the install.</h3><p>Re-run <code>m8t brain app-create --org ${org}</code>, or check the terminal.</p>`;

/** Pages served by the install server. `awaiting-install` is shown on /callback
 *  (after the App is created): its Install button navigates THIS tab to GitHub's
 *  install page, and the App's setup_url redirects the user back to /setup, which
 *  renders the `installed` page — so they never get stranded on GitHub's
 *  install-confirmation page. */
export function renderStatusPage(args: { state: InstallState; org: string; slug: string; appName: string }): string {
  const { state, org, slug } = args;
  if (state === "installed") return shell(INSTALLED_HTML(org));
  if (state === "error") return shell(ERROR_HTML(org));
  const installUrl = `https://github.com/apps/${slug}/installations/new`;
  return shell(`
<h3>✅ App created — ${args.appName}</h3>
<p>One step left: install it on <b>${org}</b>.</p>
<p><a href="${installUrl}" role="button" class="btn">Install on ${org}</a></p>
<p class="muted">You'll pick repositories on GitHub, then GitHub brings you right back here.</p>`);
}

export async function createGitHubApp(args: {
  org: string;
  port: number;
  nameSuffix: string;
  openBrowser: (url: string) => void | Promise<void>;
  pollInstallation: (app: ConvertedApp) => Promise<string>;
  onProgress?: (m: string) => void;
  deps?: { convertManifestCode?: (code: string) => Promise<ConvertedApp> };
  /** Grace before the install-poll fallback completes, giving GitHub's setup_url
   *  redirect time to land on /setup first. Tests pass a small value. */
  pollFallbackMs?: number;
}): Promise<{ app: ConvertedApp; installationId: string }> {
  const convert = args.deps?.convertManifestCode ?? convertManifestCode;
  const pollFallbackMs = args.pollFallbackMs ?? 8000;
  const base = `http://localhost:${args.port.toString()}`;
  const state = `m8t-${args.org}`;
  const redirectUrl = `${base}/callback`;
  const setupUrl = `${base}/setup`;
  let createdApp: ConvertedApp | null = null;
  let polledInstallationId: string | null = null;
  return new Promise<{ app: ConvertedApp; installationId: string }>((resolve, reject) => {
    let settled = false;
    // Declared with `let` (not const) so `complete` below can close over it
    // before the createServer() assignment — breaks the declaration cycle.
    // eslint-disable-next-line prefer-const
    let server: ReturnType<typeof createServer>;
    // Resolve + close once. Preferred signal is GitHub's setup_url redirect (the
    // user lands back on /setup); the CLI's install poll is the fallback for when
    // the browser never returns.
    const complete = (installationId: string): void => {
      if (settled || !createdApp) return;
      settled = true;
      resolve({ app: createdApp, installationId });
      setTimeout(() => { server.close(); }, 2500);
    };
    server = createServer((req, res) => {
      void (async () => {
        const u = new URL(req.url ?? "/", base);
        if (u.pathname === "/") {
          res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
          res.end(renderManifestForm({ org: args.org, state, redirectUrl, setupUrl, nameSuffix: args.nameSuffix }));
          return;
        }
        if (u.pathname === "/setup") {
          // GitHub redirects the user HERE after they install the App (setup_url).
          const id = u.searchParams.get("installation_id") ?? polledInstallationId ?? "";
          res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
          res.end(renderStatusPage({ state: "installed", org: args.org, slug: createdApp?.slug ?? "", appName: createdApp?.name ?? "" }));
          complete(id);
          return;
        }
        if (u.pathname === "/callback") {
          const code = u.searchParams.get("code");
          if (!code) { res.writeHead(400); res.end("no code"); return; }
          try {
            const app = await convert(code);
            createdApp = app;
            res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" });
            res.end(renderStatusPage({ state: "awaiting-install", org: args.org, slug: app.slug, appName: app.name }));
            // Fallback completion via the org-install poll — give the setup_url
            // redirect a grace window to land on /setup first.
            args.pollInstallation(app).then(
              (installationId) => {
                polledInstallationId = installationId;
                setTimeout(() => { complete(installationId); }, pollFallbackMs);
              },
              (e: unknown) => {
                if (settled) return;
                settled = true;
                setTimeout(() => { server.close(); }, 2500);
                reject(e instanceof Error ? e : new Error(String(e)));
              },
            );
          } catch (e) {
            res.writeHead(500); res.end(String(e));
            server.close();
            reject(e instanceof Error ? e : new Error(String(e)));
          }
          return;
        }
        res.writeHead(404);
        res.end("not found");
      })();
    });
    server.on("error", (e) => {
      reject(new LocalCliError({ code: "GH_APP_SERVER", message: `localhost server failed: ${e.message}` }));
    });
    server.listen(args.port, () => {
      const url = `${base}/`;
      args.onProgress?.(`Opening ${url} — follow the page: Create the App, then Install it on ${args.org}.`);
      void args.openBrowser(url);
    });
  });
}
